import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Fish, Heart, MessageCircle, Share2, Plus, Camera, MapPin, Calendar, User, Send, UserPlus, UserMinus, Users, Loader2 } from 'lucide-react';
import ReportButton from '../components/ReportButton';
import CatchImage from '../components/CatchImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import BadgeUnlockModal from '../components/BadgeUnlockModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Community = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [followingFeed, setFollowingFeed] = useState([]);
  const [lakes, setLakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [catchDialogOpen, setCatchDialogOpen] = useState(false);
  const [selectedCatch, setSelectedCatch] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [userProfileDialog, setUserProfileDialog] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [followingUsers, setFollowingUsers] = useState(new Set());
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [unlockedBadges, setUnlockedBadges] = useState([]);
  
  const [catchForm, setCatchForm] = useState({
    lake_id: '',
    fish_type: '',
    weight: '',
    length: '',
    bait_used: '',
    notes: '',
    image_url: ''
  });

  const fishTypes = ['Common Carp', 'Mirror Carp', 'Leather Carp', 'Ghost Carp', 'Koi Carp', 'Grass Carp'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'following') {
      fetchFollowingFeed();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [feedRes, lakesRes, followingRes] = await Promise.all([
        axios.get(`${API}/catches?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/lakes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users/${user?.id}/following`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      setFeed(feedRes.data);
      setLakes(lakesRes.data);
      setFollowingUsers(new Set(followingRes.data.map(u => u.id)));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load community feed');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowingFeed = async () => {
    try {
      const response = await axios.get(`${API}/feed/following`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowingFeed(response.data);
    } catch (error) {
      console.error('Failed to fetch following feed:', error);
    }
  };

  const handleFollowUser = async (userId, username) => {
    try {
      await axios.post(`${API}/users/${userId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowingUsers(prev => new Set([...prev, userId]));
      toast.success(`Now following ${username}`);
      
      // Update user profile if open
      if (userProfile?.id === userId) {
        setUserProfile(prev => ({ ...prev, is_following: true, followers_count: (prev.followers_count || 0) + 1 }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to follow user');
    }
  };

  const handleUnfollowUser = async (userId, username) => {
    try {
      await axios.delete(`${API}/users/${userId}/follow`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      toast.success(`Unfollowed ${username}`);
      
      // Update user profile if open
      if (userProfile?.id === userId) {
        setUserProfile(prev => ({ ...prev, is_following: false, followers_count: Math.max(0, (prev.followers_count || 1) - 1) }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfollow user');
    }
  };

  const viewUserProfile = async (userId) => {
    if (userId === user?.id) return; // Don't open profile for self
    
    try {
      setUserProfileDialog(userId);
      const response = await axios.get(`${API}/users/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(response.data);
    } catch (error) {
      toast.error('Failed to load user profile');
      setUserProfileDialog(null);
    }
  };

  const handleLike = async (catchId) => {
    try {
      await axios.post(`${API}/catches/${catchId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update both feeds
      const updateFeed = (feedArray) => feedArray.map(c => {
        if (c.id === catchId) {
          const isLiked = !c.is_liked;
          return {
            ...c,
            is_liked: isLiked,
            like_count: isLiked ? (c.like_count || 0) + 1 : Math.max(0, (c.like_count || 1) - 1)
          };
        }
        return c;
      });
      
      setFeed(updateFeed);
      setFollowingFeed(updateFeed);
    } catch (error) {
      toast.error('Failed to like catch');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB.');
      return;
    }
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Resize + compress client-side, then store as a base64 data URI directly
    // on the catch document. This (a) shows an immediate preview, (b) avoids a
    // separate upload round-trip that could silently fail, and (c) means the
    // image survives container redeploys (the previous filesystem-based upload
    // path on Emergent's ephemeral pod storage was being wiped on every deploy
    // — that's why earlier catch photos render as broken/red placeholders).
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = document.createElement('img');
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = Math.min(MAX / width, MAX / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        // 0.82 JPEG quality keeps a 1280px carp photo around 150-250KB
        const dataUri = canvas.toDataURL('image/jpeg', 0.82);
        setImagePreview(dataUri);
        setCatchForm((prev) => ({ ...prev, image_url: dataUri }));
      };
      img.onerror = () => {
        toast.error('Could not read that image. Try a different photo.');
        setImageFile(null);
      };
      img.src = ev.target.result;
    };
    reader.onerror = () => toast.error('Could not read that image.');
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setCatchForm({ ...catchForm, image_url: '' });
  };

  const handleSubmitCatch = async () => {
    if (!catchForm.lake_id || !catchForm.fish_type || !catchForm.weight) {
      toast.error('Please pick a lake, fish type, and weight before sharing.', { duration: 6000 });
      return;
    }

    setUploading(true);
    try {
      const lake = lakes.find(l => l.id === catchForm.lake_id);
      // image_url is either: (a) a data: URI populated by handleImageSelect
      // after client-side resize, (b) a pasted URL, or (c) empty string.
      const payload = {
        ...catchForm,
        lake_name: lake?.name || 'Unknown Lake',
        weight: parseFloat(catchForm.weight),
        length: catchForm.length ? parseFloat(catchForm.length) : null,
      };

      const res = await axios.post(`${API}/catches`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // High-visibility success — top-center, rich color, 5s duration globally,
      // bumped to 6s here so it stays through the dialog close animation.
      toast.success('Catch shared. Tight lines!', { duration: 6000 });
      if (res.data?.badges_unlocked?.length) {
        setUnlockedBadges(res.data.badges_unlocked);
      }
      setCatchDialogOpen(false);
      setCatchForm({
        lake_id: '',
        fish_type: '',
        weight: '',
        length: '',
        bait_used: '',
        notes: '',
        image_url: ''
      });
      setImageFile(null);
      setImagePreview(null);
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      const msg = error.response?.data?.detail
        || (error.response?.status ? `Server responded ${error.response.status}` : 'Network error — check your connection and try again.');
      toast.error(`Couldn't share catch: ${msg}`, { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  const handleShare = (catch_, platform) => {
    const shareUrl = `${window.location.origin}/community?catch=${catch_.id}`;
    const shareText = `Check out this ${catch_.weight}lb ${catch_.fish_type} caught at ${catch_.lake_name}! 🎣`;
    
    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=550,height=420'
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
          '_blank',
          'width=550,height=420'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
          '_blank'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl).then(() => {
          toast.success('Link copied to clipboard!');
        }).catch(() => {
          toast.error('Failed to copy link');
        });
        break;
      default:
        break;
    }
  };

  const getTierBadge = (tier) => {
    const styles = {
      basic: 'bg-[#2d352d] text-[#6b7d6b]',
      pro: 'bg-[#ff5e00] text-white',
      elite: 'bg-gradient-to-r from-[#c8b273] to-[#96834d] text-black'
    };
    return styles[tier] || styles.basic;
  };

  const CatchCard = ({ catch_, showFollowButton = true }) => {
    const isOwnCatch = catch_.user_id === user?.id;
    const isFollowing = followingUsers.has(catch_.user_id);
    
    return (
      <motion.div
        className="glass-card overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid={`catch-card-${catch_.id}`}
      >
        {/* User Header */}
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => viewUserProfile(catch_.user_id)}
            >
              <div className="w-10 h-10 rounded-full bg-[#141714] flex items-center justify-center">
                {catch_.user?.avatar_url ? (
                  <img src={catch_.user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[#6b7d6b]" />
                )}
              </div>
              <div>
                <p className="text-white font-bold flex items-center gap-2">
                  {catch_.user?.username || 'Angler'}
                  {catch_.user?.subscription_tier && catch_.user.subscription_tier !== 'basic' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded uppercase font-bold ${getTierBadge(catch_.user.subscription_tier)}`}>
                      {catch_.user.subscription_tier}
                    </span>
                  )}
                </p>
                <p className="text-[#6b7d6b] text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(catch_.caught_at).toLocaleDateString('en-GB', { 
                    day: 'numeric', month: 'short', year: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            
            {showFollowButton && !isOwnCatch && (
              <Button
                variant="ghost"
                size="sm"
                className={isFollowing ? 'text-[#6b7d6b] hover:text-red-400' : 'text-[#c8b273] hover:text-[#c8b273]'}
                onClick={() => isFollowing 
                  ? handleUnfollowUser(catch_.user_id, catch_.user?.username)
                  : handleFollowUser(catch_.user_id, catch_.user?.username)
                }
                data-testid={`follow-btn-${catch_.user_id}`}
              >
                {isFollowing ? (
                  <>
                    <UserMinus className="w-4 h-4 mr-1" />
                    Following
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-1" />
                    Follow
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Catch Image — universal resolver handles data URIs, absolute URLs,
            and legacy /api/uploads paths with a branded fallback when
            missing or broken. */}
        <CatchImage row={catch_} alt={`${catch_.fish_type} catch`} testId={`catch-image-${catch_.id}`} />

        {/* Catch Details */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <Fish className="w-5 h-5 text-[#c8b273]" />
                <span className="text-white font-bold">{catch_.fish_type}</span>
              </div>
              <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">
                {catch_.weight}lb
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[#8a948a] text-sm mb-3">
            <MapPin className="w-4 h-4" />
            <span>{catch_.lake_name}</span>
          </div>

          {catch_.bait_used && (
            <p className="text-[#6b7d6b] text-sm mb-2">
              <span className="text-[#8a948a]">Bait:</span> {catch_.bait_used}
            </p>
          )}

          {catch_.notes && (
            <p className="text-[#8a948a] text-sm mb-3">{catch_.notes}</p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 pt-3 border-t border-white/5">
            <button
              className={`flex items-center gap-1.5 text-sm transition-colors ${
                catch_.is_liked ? 'text-red-500' : 'text-[#6b7d6b] hover:text-red-400'
              }`}
              onClick={() => handleLike(catch_.id)}
              data-testid={`like-btn-${catch_.id}`}
            >
              <Heart className={`w-5 h-5 ${catch_.is_liked ? 'fill-current' : ''}`} />
              <span>{catch_.like_count || 0}</span>
            </button>
            <button className="flex items-center gap-1.5 text-sm text-[#6b7d6b] hover:text-[#c8b273] transition-colors">
              <MessageCircle className="w-5 h-5" />
              <span>{catch_.comments?.length || 0}</span>
            </button>
            
            {/* Share Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center gap-1.5 text-sm text-[#6b7d6b] hover:text-[#c8b273] transition-colors"
                data-testid={`share-btn-${catch_.id}`}
              >
                <Share2 className="w-5 h-5" />
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute bottom-full left-0 mb-2 w-48 bg-[#141714] rounded-lg shadow-xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-2 space-y-1">
                  <button
                    onClick={() => handleShare(catch_, 'twitter')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#8a948a] hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Share on X
                  </button>
                  <button
                    onClick={() => handleShare(catch_, 'facebook')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#8a948a] hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Share on Facebook
                  </button>
                  <button
                    onClick={() => handleShare(catch_, 'whatsapp')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#8a948a] hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Share on WhatsApp
                  </button>
                  <div className="border-t border-white/10 my-1"></div>
                  <button
                    onClick={() => handleShare(catch_, 'copy')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#8a948a] hover:text-white hover:bg-white/5 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
            {/* Report — separate from the share menu so users always see it */}
            {catch_.user_id !== user?.id && (
              <ReportButton contentType="catch" contentId={catch_.id} variant="icon" />
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  const currentFeed = activeTab === 'following' ? followingFeed : feed;

  return (
    <div className="min-h-screen" data-testid="community-page">
      {/* Hero */}
      <div 
        className="relative h-48 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1545450660-3378a7f3a364?w=1200')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/70 to-transparent" />
        <div className="relative z-10 h-full flex items-end pb-6 px-6 md:px-8">
          <div className="max-w-7xl mx-auto w-full flex justify-between items-end">
            <div>
              <p className="data-label text-[#c8b273] mb-1">Community</p>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-extrabold uppercase tracking-wider text-white">
                Catch Reports
              </h1>
            </div>
            <Dialog open={catchDialogOpen} onOpenChange={setCatchDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="share-catch-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Share Catch
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
                    Share Your Catch
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Select
                    value={catchForm.lake_id}
                    onValueChange={(v) => setCatchForm({ ...catchForm, lake_id: v })}
                  >
                    <SelectTrigger className="bg-[#141714] border-white/10 text-white">
                      <SelectValue placeholder="Select Lake *" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0d0b] border-white/10">
                      {lakes.map((lake) => (
                        <SelectItem key={lake.id} value={lake.id}>{lake.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={catchForm.fish_type}
                    onValueChange={(v) => setCatchForm({ ...catchForm, fish_type: v })}
                  >
                    <SelectTrigger className="bg-[#141714] border-white/10 text-white">
                      <SelectValue placeholder="Fish Type *" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0d0b] border-white/10">
                      {fishTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="Weight (lb) *"
                      value={catchForm.weight}
                      onChange={(e) => setCatchForm({ ...catchForm, weight: e.target.value })}
                      className="bg-[#141714] border-white/10 text-white"
                    />
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="Length (in)"
                      value={catchForm.length}
                      onChange={(e) => setCatchForm({ ...catchForm, length: e.target.value })}
                      className="bg-[#141714] border-white/10 text-white"
                    />
                  </div>

                  <Input
                    placeholder="Bait used"
                    value={catchForm.bait_used}
                    onChange={(e) => setCatchForm({ ...catchForm, bait_used: e.target.value })}
                    className="bg-[#141714] border-white/10 text-white"
                  />

                  {/* Image Upload Section */}
                  <div className="space-y-3">
                    <label className="block text-sm text-[#8a948a]">Catch Photo</label>
                    
                    {imagePreview ? (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#c8b273]/50 transition-colors bg-[#141714]">
                        <div className="flex flex-col items-center justify-center py-4">
                          <Camera className="w-8 h-8 text-[#8a948a] mb-2" />
                          <p className="text-sm text-[#8a948a]">Click to upload photo</p>
                          <p className="text-xs text-[#4a504a] mt-1">JPEG, PNG, GIF up to 10MB</p>
                        </div>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={handleImageSelect}
                          data-testid="catch-image-input"
                        />
                      </label>
                    )}
                    
                    {/* Or use URL */}
                    <div className="flex items-center gap-2 text-xs text-[#6b7d6b]">
                      <div className="flex-1 h-px bg-white/10"></div>
                      <span>or paste URL</span>
                      <div className="flex-1 h-px bg-white/10"></div>
                    </div>
                    
                    <Input
                      placeholder="Image URL (optional)"
                      value={catchForm.image_url}
                      onChange={(e) => setCatchForm({ ...catchForm, image_url: e.target.value })}
                      className="bg-[#141714] border-white/10 text-white text-sm"
                      disabled={!!imagePreview}
                    />
                  </div>

                  <Textarea
                    placeholder="Notes about your catch..."
                    value={catchForm.notes}
                    onChange={(e) => setCatchForm({ ...catchForm, notes: e.target.value })}
                    className="bg-[#141714] border-white/10 text-white min-h-[100px]"
                  />

                  <Button 
                    className="btn-primary w-full" 
                    onClick={handleSubmitCatch}
                    disabled={uploading}
                    data-testid="submit-catch-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Share Catch Report
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Feed */}
      <div className="p-6 md:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Feed Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-[#0b0d0b] border border-white/10">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black"
                data-testid="all-feed-tab"
              >
                <Users className="w-4 h-4 mr-2" />
                All Catches
              </TabsTrigger>
              <TabsTrigger 
                value="following" 
                className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black"
                data-testid="following-feed-tab"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Following
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Feed Content */}
          {currentFeed.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Fish className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
              {activeTab === 'following' ? (
                <>
                  <p className="text-[#8a948a] mb-2">No catches from people you follow</p>
                  <p className="text-[#6b7d6b] text-sm">Follow some anglers to see their catches here!</p>
                </>
              ) : (
                <p className="text-[#8a948a]">No catch reports yet. Be the first to share!</p>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <AnimatePresence>
                {currentFeed.map((catch_) => (
                  <CatchCard key={catch_.id} catch_={catch_} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* User Profile Dialog */}
      <Dialog open={!!userProfileDialog} onOpenChange={() => { setUserProfileDialog(null); setUserProfile(null); }}>
        <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">User Profile</DialogTitle>
          </DialogHeader>
          {userProfile ? (
            <>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-[#141714] flex items-center justify-center">
                  {userProfile.avatar_url ? (
                    <img src={userProfile.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-[#6b7d6b]" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white flex items-center gap-2">
                    {userProfile.username}
                    {userProfile.subscription_tier && userProfile.subscription_tier !== 'basic' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded uppercase font-bold ${getTierBadge(userProfile.subscription_tier)}`}>
                        {userProfile.subscription_tier}
                      </span>
                    )}
                  </h2>
                  {userProfile.location && (
                    <p className="text-[#8a948a] text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {userProfile.location}
                    </p>
                  )}
                </div>
              </div>

              {userProfile.bio && (
                <p className="text-[#8a948a] mb-4">{userProfile.bio}</p>
              )}

              <div className="flex items-center gap-6 mb-4 p-4 bg-[#141714] rounded-lg">
                <div className="text-center">
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">{userProfile.catch_count || 0}</p>
                  <p className="text-[#6b7d6b] text-xs uppercase">Catches</p>
                </div>
                <div className="text-center">
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">{userProfile.followers_count || 0}</p>
                  <p className="text-[#6b7d6b] text-xs uppercase">Followers</p>
                </div>
                <div className="text-center">
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white">{userProfile.following_count || 0}</p>
                  <p className="text-[#6b7d6b] text-xs uppercase">Following</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  className={userProfile.is_following ? 'btn-ghost flex-1' : 'btn-primary flex-1'}
                  onClick={() => userProfile.is_following 
                    ? handleUnfollowUser(userProfile.id, userProfile.username)
                    : handleFollowUser(userProfile.id, userProfile.username)
                  }
                  data-testid="profile-follow-btn"
                >
                  {userProfile.is_following ? (
                    <>
                      <UserMinus className="w-4 h-4 mr-2" />
                      Unfollow
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Follow
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-[#c8b273] text-[#c8b273] hover:bg-[#c8b273] hover:text-black"
                  onClick={() => {
                    setUserProfileDialog(null);
                    navigate(`/messages?user=${userProfile.id}`);
                  }}
                  data-testid="profile-message-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>

              {userProfile.recent_catches && userProfile.recent_catches.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-['Barlow_Condensed'] text-sm font-bold uppercase tracking-wider text-[#c8b273] mb-3">
                    Recent Catches
                  </h3>
                  <div className="space-y-2">
                    {userProfile.recent_catches.map((catch_) => (
                      <div key={catch_.id} className="flex items-center gap-3 p-2 bg-[#141714] rounded">
                        <CatchImage row={catch_} aspectRatio="square" rounded className="w-10 h-10 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{catch_.fish_type}</p>
                          {catch_.lake_name && <p className="text-[#6b7d6b] text-xs truncate">{catch_.lake_name}</p>}
                        </div>
                        <span className="font-['Barlow_Condensed'] text-[#c8b273] font-bold flex-shrink-0">{catch_.weight}lb</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 text-[#c8b273] animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Achievement celebration */}
      <BadgeUnlockModal
        badges={unlockedBadges}
        onClose={() => setUnlockedBadges([])}
      />
    </div>
  );
};

export default Community;
